import { Close } from '@mui/icons-material';
import React, { useCallback, useEffect, useRef } from 'react';

import { LinkButton } from '../link-button';
import { Text } from '../text';
import {
  StyledCloseButtonWrap,
  StyledHeader,
  StyledModalContainer,
  StyledOverlay,
} from './styles';
import type { ModalHeaderProps, ModalProps, RenderModalContentProps } from './types';

let openModalCount = 0;
let savedBodyOverflow = '';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const useFocusManagement = (isOpen: boolean, containerRef: React.RefObject<HTMLDivElement | null>): void => {
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const firstFocusable = containerRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? containerRef.current).focus();
    }
  }, [isOpen, containerRef]);
};

const useBodyOverflowLock = (isOpen: boolean): void => {
  useEffect(() => {
    if (isOpen) {
      if (openModalCount === 0) {
        savedBodyOverflow = document.body.style.overflow;
      }
      openModalCount++;
      document.body.style.overflow = 'hidden';
      return () => {
        openModalCount = Math.max(0, openModalCount - 1);
        if (openModalCount === 0) {
          document.body.style.overflow = savedBodyOverflow;
        }
      };
    }
  }, [isOpen]);
};

const handleModalKeyDown = (
  event: React.KeyboardEvent,
  onClose: (() => void) | undefined,
  closeOnEscape: boolean,
  container: HTMLDivElement | null,
): void => {
  if (event.key === 'Escape' && onClose && closeOnEscape) {
    onClose();
    return;
  }
  if (event.key === 'Tab' && container) {
    trapFocus(event, container);
  }
};

const ModalHeader: React.FC<ModalHeaderProps> = ({
  hideTitle,
  showCloseButton,
  title,
  onClose,
  closeButtonLabel,
}) => (
  <StyledHeader>
    {!hideTitle && (
      <Text size="xl" as="h2" weight="bold">
        {title}
      </Text>
    )}
    {showCloseButton && onClose && (
      <StyledCloseButtonWrap>
        <LinkButton
          onClick={onClose}
          label={closeButtonLabel}
          icon={<Close />}
          testID="close-modal-button"
        />
      </StyledCloseButtonWrap>
    )}
  </StyledHeader>
);

const isAtStartOfFocusables = (activeIndex: number): boolean => activeIndex <= 0;

const isAtEndOfFocusables = (activeIndex: number, focusableLength: number): boolean =>
  activeIndex === -1 || activeIndex === focusableLength - 1;

const handleTabWrap = (
  shouldWrap: boolean,
  elementToFocus: HTMLElement | undefined,
  event: React.KeyboardEvent,
): void => {
  if (shouldWrap && elementToFocus) {
    event.preventDefault();
    elementToFocus.focus();
  }
};

const handleBackwardTab = (
  activeIndex: number,
  focusable: HTMLElement[],
  event: React.KeyboardEvent,
): void => {
  const last = focusable[focusable.length - 1];
  handleTabWrap(isAtStartOfFocusables(activeIndex), last, event);
};

const handleForwardTab = (
  activeIndex: number,
  focusable: HTMLElement[],
  event: React.KeyboardEvent,
): void => {
  const first = focusable[0];
  handleTabWrap(isAtEndOfFocusables(activeIndex, focusable.length), first, event);
};

const trapFocus = (event: React.KeyboardEvent, container: HTMLDivElement): void => {
  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (!first || !last) {
    event.preventDefault();
    return;
  }

  const activeIndex = focusable.indexOf(document.activeElement as HTMLElement);

  if (event.shiftKey) {
    handleBackwardTab(activeIndex, focusable, event);
  } else {
    handleForwardTab(activeIndex, focusable, event);
  }
};

const renderModalContent = (props: RenderModalContentProps): React.ReactNode => {
  const { showHeader, hideTitle, showCloseButton, title, onClose, closeButtonLabel, children } = props;
  return (
    <>
      {showHeader && (
        <ModalHeader
          hideTitle={hideTitle}
          showCloseButton={showCloseButton}
          title={title}
          onClose={onClose}
          closeButtonLabel={closeButtonLabel || ''}
        />
      )}
      {children}
    </>
  );
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  closeButtonLabel = '',
  children,
  width,
  showCloseButton = false,
  hideTitle = false,
  isSecondaryModal = false,
  role = 'dialog',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  labelledBy,
  describedBy,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const showHeader = !hideTitle || showCloseButton;

  useFocusManagement(isOpen, modalRef);
  useBodyOverflowLock(isOpen);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) =>
      handleModalKeyDown(event, onClose, closeOnEscape, modalRef.current),
    [closeOnEscape, onClose],
  );

  if (!isOpen) return null;

  return (
    <div>
      <StyledOverlay
        data-testid="modal-overlay"
        onClick={closeOnOverlayClick ? onClose : undefined}
        isSecondaryModal={isSecondaryModal}
      />
      <StyledModalContainer
        role={role}
        aria-modal="true"
        aria-label={!labelledBy ? title : undefined}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
        ref={modalRef}
        onKeyDown={handleKeyDown}
        $width={width}
        isSecondaryModal={isSecondaryModal}
      >
        {renderModalContent({
          showHeader,
          hideTitle,
          showCloseButton,
          title,
          onClose,
          closeButtonLabel,
          children,
        })}
      </StyledModalContainer>
    </div>
  );
};
